import { memo } from 'react';

interface SkeletonProps {
  className?: string;
  width?: string | number;
  height?: string | number;
  rounded?: 'none' | 'sm' | 'md' | 'lg' | 'full';
  animate?: boolean;
}

/**
 * Base skeleton component for loading states
 */
export const Skeleton = memo(function Skeleton({
  className = '',
  width,
  height,
  rounded = 'md',
  animate = true,
}: SkeletonProps) {
  const roundedClasses = {
    none: '',
    sm: 'rounded-sm',
    md: 'rounded-md',
    lg: 'rounded-lg',
    full: 'rounded-full',
  };

  const style: React.CSSProperties = {};
  if (width) style.width = typeof width === 'number' ? `${width}px` : width;
  if (height) style.height = typeof height === 'number' ? `${height}px` : height;

  return (
    <div
      className={`bg-zinc-800 ${roundedClasses[rounded]} ${animate ? 'animate-pulse' : ''} ${className}`}
      style={style}
      aria-hidden="true"
    />
  );
});

/**
 * Skeleton for text lines
 */
export const SkeletonText = memo(function SkeletonText({
  lines = 1,
  className = '',
}: {
  lines?: number;
  className?: string;
}) {
  return (
    <div className={`space-y-2 ${className}`} aria-hidden="true">
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton
          key={i}
          height={16}
          width={i === lines - 1 && lines > 1 ? '75%' : '100%'}
          rounded="sm"
        />
      ))}
    </div>
  );
});

/**
 * Skeleton for avatar/profile images
 */
export const SkeletonAvatar = memo(function SkeletonAvatar({
  size = 40,
  className = '',
}: {
  size?: number;
  className?: string;
}) {
  return (
    <Skeleton
      width={size}
      height={size}
      rounded="full"
      className={className}
    />
  );
});

/**
 * Skeleton for cards
 */
export const SkeletonCard = memo(function SkeletonCard({
  className = '',
  showAvatar = false,
  lines = 3,
  children,
}: {
  className?: string;
  showAvatar?: boolean;
  lines?: number;
  children?: React.ReactNode;
}) {
  return (
    <div
      className={`rounded-xl border border-zinc-800 bg-zinc-900 p-4 ${className}`}
      aria-hidden="true"
    >
      {showAvatar && (
        <div className="mb-4 flex items-center gap-3">
          <SkeletonAvatar size={40} />
          <div className="flex-1">
            <Skeleton height={16} width="60%" className="mb-2" />
            <Skeleton height={12} width="40%" />
          </div>
        </div>
      )}
      {children || <SkeletonText lines={lines} />}
    </div>
  );
});

/**
 * Skeleton for table rows
 */
export const SkeletonTableRow = memo(function SkeletonTableRow({
  columns = 5,
  className = '',
}: {
  columns?: number;
  className?: string;
}) {
  return (
    <div
      className={`flex items-center gap-4 border-b border-zinc-800 p-4 ${className}`}
      aria-hidden="true"
    >
      {Array.from({ length: columns }).map((_, i) => (
        <Skeleton
          key={i}
          height={16}
          className="flex-1"
          width={i === 0 ? '30%' : undefined}
        />
      ))}
    </div>
  );
});

/**
 * Skeleton for list items (like lead list)
 */
export const SkeletonListItem = memo(function SkeletonListItem({
  className = '',
}: {
  className?: string;
}) {
  return (
    <div
      className={`flex items-start gap-3 border-b border-zinc-800 p-4 ${className}`}
      aria-hidden="true"
    >
      <SkeletonAvatar size={36} />
      <div className="flex-1 space-y-2">
        <div className="flex items-center justify-between">
          <Skeleton height={16} width="50%" />
          <Skeleton height={20} width={60} rounded="full" />
        </div>
        <Skeleton height={12} width="70%" />
        <div className="flex items-center gap-4">
          <Skeleton height={12} width={60} />
          <Skeleton height={12} width={80} />
          <Skeleton height={12} width={100} />
        </div>
      </div>
    </div>
  );
});

/**
 * Skeleton for stats/metric cards
 */
export const SkeletonStat = memo(function SkeletonStat({
  className = '',
}: {
  className?: string;
}) {
  return (
    <div
      className={`rounded-xl border border-zinc-800 bg-zinc-900 p-4 ${className}`}
      aria-hidden="true"
    >
      <Skeleton height={12} width="40%" className="mb-2" />
      <Skeleton height={32} width="60%" className="mb-1" />
      <Skeleton height={10} width="30%" />
    </div>
  );
});

/**
 * Skeleton for detail panel
 */
export const SkeletonDetailPanel = memo(function SkeletonDetailPanel({
  className = '',
}: {
  className?: string;
}) {
  return (
    <div className={`space-y-6 p-6 ${className}`} aria-hidden="true">
      {/* Header */}
      <div className="flex items-start gap-4">
        <SkeletonAvatar size={48} />
        <div className="flex-1">
          <Skeleton height={24} width="60%" className="mb-2" />
          <Skeleton height={14} width="40%" />
        </div>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-4 gap-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <SkeletonStat key={i} />
        ))}
      </div>

      {/* Content sections */}
      <SkeletonCard lines={4} />
      <SkeletonCard lines={3} />
    </div>
  );
});

/**
 * Leads page skeleton
 */
export const SkeletonLeadsPage = memo(function SkeletonLeadsPage() {
  return (
    <div className="flex h-full" aria-label="Loading leads...">
      {/* Left panel - list */}
      <div className="w-80 border-r border-zinc-800">
        <div className="border-b border-zinc-800 p-4">
          <Skeleton height={40} rounded="lg" />
        </div>
        <div className="p-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <SkeletonListItem key={i} />
          ))}
        </div>
      </div>

      {/* Right panel - detail */}
      <div className="flex-1">
        <SkeletonDetailPanel />
      </div>
    </div>
  );
});

/**
 * Dashboard skeleton
 */
export const SkeletonDashboard = memo(function SkeletonDashboard() {
  return (
    <div className="space-y-6 p-6" aria-label="Loading dashboard...">
      {/* Stats row */}
      <div className="grid grid-cols-4 gap-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <SkeletonStat key={i} />
        ))}
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-2 gap-6">
        <Skeleton height={300} rounded="lg" />
        <Skeleton height={300} rounded="lg" />
      </div>

      {/* Table */}
      <SkeletonCard lines={0}>
        {Array.from({ length: 5 }).map((_, i) => (
          <SkeletonTableRow key={i} columns={6} />
        ))}
      </SkeletonCard>
    </div>
  );
});

export default Skeleton;
